import { CSSProperties, FC, ReactNode } from 'react';
import { Avatar, Button, Col, Row, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

interface PreferenceModalHeaderProps {
    description?: ReactNode;
    extra?: ReactNode;
    title: ReactNode;
    titleLevel?: 1 | 2 | 3 | 4 | 5;
}

export const PreferenceModalHeader: FC<PreferenceModalHeaderProps> = ({
    description,
    extra,
    title,
    titleLevel = 5,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            justify='space-between'
            wrap={false}
            style={{
                borderBottom: `1px solid ${token.colorBorder}`,
                boxSizing: 'border-box',
                minHeight: '4.75rem',
                padding: '0.75rem 1rem',
            }}
        >
            <Col flex='auto' style={{ minWidth: 0 }}>
                <Typography.Title level={titleLevel} style={{ margin: 0 }}>
                    {title}
                </Typography.Title>
                {description ? (
                    <div style={{ color: token.colorTextTertiary, marginTop: '0.125rem' }}>{description}</div>
                ) : null}
            </Col>
            {extra ? <Col flex='none'>{extra}</Col> : null}
        </Row>
    );
};

interface PreferenceModalSiderOptionProps {
    active: boolean;
    children: ReactNode;
    disabled?: boolean;
    onClick?: () => void;
    style?: CSSProperties;
}

export const PreferenceModalSiderOption: FC<PreferenceModalSiderOptionProps> = ({
    active,
    children,
    disabled = false,
    onClick,
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row align='middle' gutter={10} wrap={false}>
            <Col flex='auto' style={{ minWidth: 0 }}>
                <Button
                    block
                    disabled={disabled}
                    style={{
                        backgroundColor: active ? token.colorBgContainerDisabled : 'transparent',
                        borderColor: token.colorBorderSecondary,
                        justifyContent: 'flex-start',
                        minWidth: 0,
                        ...style,
                    }}
                    onClick={onClick}
                >
                    {children}
                </Button>
            </Col>
            <Col flex='none' style={{ width: '0.125rem' }}>
                <span
                    aria-hidden
                    style={{
                        backgroundColor: active ? token.colorPrimary : 'transparent',
                        borderRadius: token.borderRadiusLG,
                        display: 'block',
                        height: '1.5rem',
                        transition: 'background-color 160ms ease',
                        width: '0.125rem',
                    }}
                />
            </Col>
        </Row>
    );
};

interface PreferenceModalIconAvatarProps {
    icon: ReactNode;
    size?: number | 'large' | 'small';
    style?: CSSProperties;
}

export const PreferenceModalIconAvatar: FC<PreferenceModalIconAvatarProps> = ({ icon, size, style }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Avatar
            icon={icon}
            shape='square'
            size={size}
            style={{
                backgroundColor: token.colorBorderBg,
                border: `1px solid ${token.colorBorder}`,
                color: token.colorTextTertiary,
                flex: 'none',
                ...style,
            }}
        />
    );
};
